import { uploadFile } from '@services/repair'
import Box from '@shared/Box'
import { concat, isNil, remove } from 'ramda'
import React from 'react'
import UploadButton from 'src/components/UploadButton'

type Props = {
  isEdit: boolean
  defaultFiles: any[]
  uploadOwner: string
  onChange?: (val?: any) => void
  maxCount?: number
  maxImgSize?: number
  maxWidth?: number
  maxHeight?: number
  isModalMode?: boolean
  title?: string
  isMultiple?: boolean
}
function PhotosUpload({
  defaultFiles,
  isEdit,
  onChange,
  uploadOwner,
  maxCount = Infinity,
  maxImgSize = 1,
  maxWidth,
  maxHeight,
  isModalMode,
  title = '',
  isMultiple = true,
  ...props
}: Props) {
  const [pictures, setPictures] = React.useState<any[]>(defaultFiles)
  const [newPics, setNewPics] = React.useState<any[]>([])
  const [loading, setLoading] = React.useState(false)

  const handleUpdatePics = async (
    file: any,
    pSeq?: number,
    isNew?: boolean
  ) => {
    setLoading(true)
    const res = await uploadFile({ file, uploadOwner })

    const { previewUrl, path, hash } = res.data
    const data = { url: previewUrl, filePath: path, hash }

    // 如果是單張模式，覆蓋原圖
    if (!isMultiple) {
      if (isNew) {
        setNewPics([data])
        setPictures([])
      } else {
        setPictures([data])
        setNewPics([])
      }
    } else {
      const handler = isNew ? setNewPics : setPictures
      handler(prev => {
        if (pSeq !== undefined) {
          const pic = [...prev]
          pic[pSeq] = data
          return pic
        }
        return [...prev, data]
      })
    }

    setLoading(false)
  }

  const handleRemovePics = (pSeq: any) => {
    setPictures((prev: any) => {
      const pic = remove(pSeq, 1, prev)
      return pic
    })
  }

  const handleRemoveNewPics = (pSeq: any) => {
    setNewPics((prev: any) => {
      const pic = remove(pSeq, 1, prev)
      return pic
    })
  }

  React.useEffect(() => {
    onChange && onChange({ pictures, newPics })
  }, [pictures, newPics]) //eslint-disable-line

  React.useEffect(() => {
    if (!isEdit) {
      setPictures(defaultFiles)
      setNewPics([])
    }
  }, [isEdit, defaultFiles])

  const RenderMaxCountContent = () => {
    if (!isEdit) return null
    if (isNil(maxCount)) return null
    if (!isMultiple && concat(pictures, newPics).length >= 1) return null
    if (concat(pictures, newPics).length >= maxCount) return null

    return (
      <Box display="inline-block">
        <UploadButton.ImageUpload
          maxImgSize={maxImgSize}
          maxWidth={maxWidth}
          maxHeight={maxHeight}
          isModalMode={isModalMode}
          onUpdate={(file, pSeq) => handleUpdatePics(file, pSeq, true)}
        />
      </Box>
    )
  }

  return (
    <>
      {pictures.map((x: any, idx) => (
        <Box display="inline-block" key={idx}>
          <UploadButton.ImageUpload
            maxImgSize={maxImgSize}
            maxWidth={maxWidth}
            maxHeight={maxHeight}
            isModalMode={isModalMode}
            modalData={concat(pictures, newPics)}
            title={title}
            value={x}
            readOnly={!isEdit}
            pictureSeq={idx}
            loading={loading}
            onUpdate={handleUpdatePics}
            onRemove={handleRemovePics}
          />
        </Box>
      ))}
      {newPics.map((x: any, idx) => (
        <Box display="inline-block" key={idx}>
          <UploadButton.ImageUpload
            maxImgSize={maxImgSize}
            maxWidth={maxWidth}
            maxHeight={maxHeight}
            isModalMode={isModalMode}
            modalData={concat(pictures, newPics)}
            title={title}
            value={x}
            pictureSeq={idx}
            readOnly={!isEdit}
            loading={loading}
            onRemove={handleRemoveNewPics}
            onUpdate={(file, pSeq) => handleUpdatePics(file, pSeq, true)}
          />
        </Box>
      ))}
      {RenderMaxCountContent()}
    </>
  )
}
export default React.memo(PhotosUpload)
